/**
 * Продуктовые константы Баллов (план §4). Магических чисел в коде быть не должно —
 * всё берётся отсюда.
 *
 * ЗАКРЫТЫЙ КОНТУР (план §2.3) — четыре условия, нарушение любого превращает
 * программу лояльности в эмиссию электронных денег:
 *   1. Баллы не обмениваются на деньги (в т.ч. при отказе — возврат Баллами).
 *   2. Баллы не передаются между пользователями.
 *   3. Баллы не покупаются.
 *   4. Баллы гасятся только за услуги того же юрлица и его сервисов.
 */

/** Курс: 100 Баллов = 1 USD. Опубликован и фиксирован. */
export const POINTS_PER_USD = 100;

/**
 * Шаг ползунка погашения: 10 Баллов, то есть 10 центов (решение владельца
 * 23.08.2026).
 *
 * Отдельная константа, а не `POINTS_PER_USD`: курс и шаг — разные понятия, и
 * склеив их однажды, мы поменяем курс и незаметно перестроим интерфейс. Раньше
 * шаг равнялся курсу, и ползунок ходил ровно по доллару — человек с 1 250
 * Баллами не мог отдать 1 250.
 *
 * Округления не возникает: 10 Баллов = 10 minor units ровно.
 */
export const POINTS_REDEEM_STEP = 10;

/** Доля чека, которую можно закрыть Баллами. Регулируется `max_points_share_pct` у SKU. */
export const DEFAULT_MAX_POINTS_SHARE_PCT = 50;

/**
 * Первый вход в сервис экосистемы — 100 Баллов (решение владельца 23.08.2026).
 *
 * Выдаётся ОДИН раз на пару «человек ↔ сервис», потолка на число сервисов нет.
 * Обоснование владельца: Баллы закрывают не больше 50 % цены и тратятся только
 * у нас, поэтому начисление, которое приводит человека в другой сервис
 * экосистемы, окупается вовлечением.
 *
 * Что это значит для эмиссии, чтобы решение было принято с открытыми глазами:
 * восемь вознаграждаемых сервисов × 100 = 800 Баллов потолка на человека сверх
 * регистрационных 300. Это класс B, он живёт на месячном бюджете в 5 % выручки;
 * пока выручки нет, бюджет выберут первые же люди — смотреть его надо в первый
 * месяц, а не в конце квартала (строка в `docs/LAUNCH-REGISTRY.md`).
 *
 * `iec-id` в число вознаграждаемых НЕ входит: в единый профиль человек попадает
 * при входе в любой сервис, и за это уже платятся 300 приветственных. Портал
 * (`exchange`) — считается, как любой другой сервис.
 */
export const SERVICE_ACTIVATED_REWARD = 100;

/** Сервис, вход в который наградой не считается: это мы сами. */
export const SELF_SERVICE_ID = "iec-id";

/**
 * Через сколько дней тишины по счёту напоминаем о незабранных Баллах.
 *
 * Отсчёт от ПОСЛЕДНЕГО начисления, а не от регистрации: человеку, которому
 * вчера начислили, напоминать о существовании Баллов незачем. Письмо уходит
 * один раз за всё время — однократность держит UNIQUE ключа отправки.
 */
export const TASKS_REMINDER_IDLE_DAYS = 7;

/** Сгорание: 12 месяцев без движения по счёту. */
export const POINTS_EXPIRY_MONTHS = 12;

/** За сколько дней предупреждаем о сгорании. */
export const POINTS_EXPIRY_WARNING_DAYS = [30, 7] as const;

/**
 * Суточные потолки класса B («был активен»), в Баллах.
 *
 * Значения — решение владельца от 21.08.2026 (Д6 плана ремедиации) и план §4.2:
 * 300 / 1 000 / 3 000 при множителях ×1 / ×2 / ×3. Прежняя редакция кода
 * ушла вперёд плана на 1500/3000/4500 «чтобы дорогое достижение не резалось»;
 * решение продукта — вернуться к плану.
 *
 * ЧТО ЭТО ЗНАЧИТ НА ПРАКТИКЕ (знать до того, как чинить как баг): самые дорогие
 * события «класса B» стоят 500 — ступень `ACHIEVEMENT_MAJOR_REWARD`. На free с
 * потолком 300 они урезались бы, и человек получал бы меньше, чем показала
 * витрина. Противоречие разрешено НЕ потолком: решением от 21.08.2026 они
 * переведены в разовые (`ONE_TIME_ACHIEVEMENT_CODES`) — случаются один раз в
 * жизни аккаунта и ограничены однократностью, а не дросселем.
 *
 * Потолок остаётся рельсом против ФЕРМЫ повторяемых событий: серия, публикация
 * курса, активация приглашённого, четыре повторяемых достижения Портала — всё,
 * что можно сделать «ещё раз завтра».
 *
 * Потолок СУММАРНЫЙ за сутки, а не «на событие»: `clampToDailyCap` считает
 * `remaining = cap - usedToday`. Ставка ниже потолка от урезания не защищена —
 * она урезается остатком дня, — поэтому «помещается под потолок» и «не будет
 * урезана» это разные утверждения, и второе неверно ни для одной ставки.
 */
export const DAILY_ACTIVITY_CAP = {
	free: 300,
	plus: 1000,
	pro: 3000,
} as const;

/**
 * Множитель начислений класса B и вех по уровню подписки.
 *
 * Целые числа не случайно: Баллы целые (инвариант №8), и дробный множитель
 * добавил бы ещё одно округление в самый горячий расчёт. Класс A НЕ множится —
 * он процент от реальных денег и самофинансируется.
 */
export const EARN_MULTIPLIER = {
	free: 1,
	plus: 2,
	pro: 3,
} as const;

/** Доля валовой выручки, ограничивающая месячную эмиссию класса B. */
export const CLASS_B_BUDGET_SHARE_PCT = 5;

/** Порог, с которого коэффициенты класса B автоматически снижаются. */
export const CLASS_B_BUDGET_THROTTLE_AT_PCT = 80;

/**
 * ПОЛ месячного бюджета класса B в minor units — «бюджет никогда не 0» (Д6).
 *
 * Зачем: бюджет = доля выручки, а выручка в первый месяц запуска равна нулю.
 * Без пола первое же начисление класса B по всей экосистеме исчерпывало бюджет
 * на 100%, коэффициент падал в ноль и кран стоял закрытым до первой продажи —
 * то есть программа лояльности не работала ровно тогда, когда она нужна.
 *
 * Значение переопределяется переменной окружения `BUDGET_CLASS_B_FLOOR_MINOR`.
 */
export const BUDGET_CLASS_B_FLOOR_MINOR = 50_000;

/**
 * Ставка класса A («привёл платящего») — процент от НАШЕЙ ЧИСТОЙ части платежа
 * приглашённого (Д6). Одна на все виды платежей: та же, что `referral_rate = 20`
 * в AffiliateWP на `iec.study` (`.claude/research/06-iec-study-affiliate.md` §5),
 * поэтому переезд партнёров не меняет им условия.
 */
export const CLASS_A_RATE_PCT = 20;

/**
 * Потолок класса A на ОДИН платёж, в Баллах (Д6). Дорогая программа за $10 000
 * дала бы 200 000 Баллов ($2 000) одному человеку — сумма, которую никто не
 * закладывал в маржу сделки. Потолок режет хвост, а не типовой платёж:
 * 5 000 Баллов = $50 = 20% от $250.
 */
export const CLASS_A_MAX_POINTS_PER_PAYMENT = 5_000;

/**
 * Шкала класса A по видам платежей. Ставка сейчас одна (`CLASS_A_RATE_PCT`);
 * таблица остаётся, потому что витрина показывает правила по видам платежей, а
 * различать их по ставке продукт может начать в любой момент — тогда меняются
 * значения здесь, а не формула в двух модулях.
 */
export const CLASS_A_RATES = {
	/** Приглашённый оплатил программу (стажировка/обмен). */
	programmeReferrer: CLASS_A_RATE_PCT,
	/** Первый платёж подписки сервиса. */
	subscriptionFirstReferrer: CLASS_A_RATE_PCT,
	/** Продление подписки в первые 12 месяцев. */
	subscriptionRenewalReferrer: CLASS_A_RATE_PCT,
	/** Платный курс каталога. */
	courseReferrer: CLASS_A_RATE_PCT,
} as const;

/**
 * Награда класса A в Баллах — ЕДИНСТВЕННАЯ формула на всю кодовую базу.
 *
 * Была продублирована в `points/_internal/calc.ts` и
 * `referrals/_internal/rewards.ts`: обе брали числа отсюда, но округляли каждая
 * у себя, и расхождение на один Балл было вопросом времени.
 *
 * Округление — ВНИЗ: при делении «в пользу дома» эмиссия не может превысить
 * заявленный процент из-за копеечного остатка. Потолок применяется ПОСЛЕ
 * округления, к результату в Баллах.
 */
export function classAPointsFor(
	basisMinor: number,
	ratePct: number = CLASS_A_RATE_PCT,
	maxPoints: number = CLASS_A_MAX_POINTS_PER_PAYMENT,
): number {
	const basis = Math.trunc(basisMinor);
	if (!Number.isFinite(basis) || basis <= 0) return 0;
	const rewardMinor = Math.floor((basis * ratePct) / 100);
	return Math.min(minorToPoints(rewardMinor), maxPoints);
}

/** Окно возврата, после которого начисляется класс A. */
export const CLASS_A_REFUND_WINDOW_DAYS = 30;

/** Вехи обучения (план §8) — выплаты в Баллах. */
export const MILESTONE_REWARDS = {
	/** Программа каталога доведена до `mastered`. Одна программа = одна выплата навсегда. */
	programmeMastered: 1000,
	/** Достигнут уровень по накопленным ЕЗ. */
	levelReached: 300,
	/** Пройден сезон. */
	seasonCompleted: 500,
} as const;

/**
 * Награда за завершённый онбординг (класс B).
 *
 * Это единственное начисление, которое получает аккаунт, не принёсший ни
 * рубля выручки и никого не пригласивший, — то есть ровно та сумма, которую
 * стоит ферма из одного аккаунта. KYC у нас нет и не будет (закрытый контур
 * его не требует), поэтому защита здесь только экономическая: 300 Баллов = $3
 * СКИДКИ, воспользоваться которой можно, лишь заплатив не меньше того же
 * (погашение ограничено половиной чека).
 */
export const ONBOARDING_REWARD = 300;

/** Награда за первое фото профиля (класс B, разовая). */
export const PHOTO_ADDED_REWARD = 50;

/**
 * Соответствие `reasonCode` входящего события и типа вехи.
 *
 * Сервис присылает ФАКТ («программа освоена»), а сумму берём из
 * `MILESTONE_REWARDS` — присланное число не авторитетно (инвариант №9).
 */
export const MILESTONE_BY_REASON = {
	programme_completed: "programmeMastered",
	course_completed: "programmeMastered",
	level_reached: "levelReached",
	season_completed: "seasonCompleted",
} as const satisfies Record<string, keyof typeof MILESTONE_REWARDS>;

/**
 * Приветственный бонус приглашённому — начисляется в момент АКТИВАЦИИ, а не
 * регистрации, и после окна отложенной награды (`anticheat.ts`).
 */
export const REFERRAL_WELCOME_REWARD = 300;

/**
 * Награда пригласившему за активацию приглашённого.
 *
 * Жила магической константой в `referrals/_internal/rewards.ts` с TODO
 * «перенести в контракты» — это прямое нарушение архитектурного правила №8, и
 * оно там же было записано. Перенесена.
 *
 * Класс B, а не A: денег в этот момент никто не заплатил. Пара
 * «активация + приветствие» = 600 Баллов ($6) на двоих, под суточным потолком
 * и месячным бюджетом.
 */
export const REFERRAL_ACTIVATION_REWARD = 300;

/**
 * Выплаты класса B («был активен») по причине события, в Баллах.
 *
 * Суммы намеренно малые: класс B ограничен суточным потолком
 * (`DAILY_ACTIVITY_CAP`) и месячным бюджетом в 5% выручки. Причина, которой
 * нет в таблице, начисления НЕ даёт — молчаливого «ну хоть сколько-нибудь»
 * здесь быть не должно, иначе эмиссия перестаёт быть предсказуемой.
 */
export const ACTIVITY_REWARDS = {
	profile_completed: 100,
	onboarding_completed: ONBOARDING_REWARD,
	service_activated: SERVICE_ACTIVATED_REWARD,
	photo_added: PHOTO_ADDED_REWARD,
	referral_activated: REFERRAL_ACTIVATION_REWARD,
	streak_week: 50,
	course_published: 200,
} as const;

export type ActivityReasonCode = keyof typeof ACTIVITY_REWARDS;

/**
 * Достижения — НЕ плоская ставка.
 *
 * Ключа `achievement` в `ACTIVITY_REWARDS` намеренно нет: пока он там был,
 * `activityReward("achievement")` отдавал одно и то же число на все коды, и
 * дорогое достижение стоило столько же, сколько «зарегистрировался на вебинар».
 */

/** Ставка рядового достижения — якорь шкалы. */
export const ACHIEVEMENT_BASE_REWARD = 50;

/**
 * Ставка заметного достижения — вдвое от якоря.
 *
 * ВНИМАНИЕ: ставка ПРЕДЛОЖЕНА 26.08.2026 вместе с остальными 23 кодами Портала
 * и владельцем не подтверждена (строка в `docs/LAUNCH-REGISTRY.md` §6.4).
 * Средняя ступень понадобилась потому, что шкала «50 или 500» ставила
 * «50 реакций на пост» либо вровень с «заполнил соцсети», либо вровень со
 * «стал экспертом», и оба варианта читаются как ошибка. Кратность якорю выбрана
 * намеренно — ступени шкалы должны сравниваться друг с другом, а не быть тремя
 * независимыми числами.
 *
 * ЭТА СТУПЕНЬ УРЕЗАТЬСЯ МОЖЕТ, И ЭТО ОСОЗНАННЫЙ РАЗМЕН. Суточный потолок —
 * СУММАРНЫЙ за день, а не «на событие»: `clampToDailyCap` считает
 * `remaining = cap - usedToday`. При `usedToday = 250` заметное достижение
 * начислится как 50 из 100, `capped: true`, и ключ идемпотентности при
 * частичном урезании расходуется НАВСЕГДА (`accrual-core.ts`) — недостача не
 * доплачивается ни в какой следующий день. Мириться с этим можно только потому,
 * что заметная ступень целиком помещается под потолок free (100 ≤ 300): худший
 * случай — «получил меньше в день, когда и так набрал много», а не «обещание
 * витрины недостижимо в принципе». Всё, что ДОРОЖЕ потолка, недостижимо в
 * принципе, и потому обязано лежать в `ONE_TIME_ACHIEVEMENT_CODES`.
 */
export const ACHIEVEMENT_NOTABLE_REWARD = ACHIEVEMENT_BASE_REWARD * 2;

/**
 * Ставка весомого достижения. Больше суточного потолка free (300) — значит,
 * каждый код этой ступени обязан лежать в `ONE_TIME_ACHIEVEMENT_CODES`, иначе
 * человек получит меньше, чем ему показала витрина.
 *
 * Отсюда жёсткое следствие: на ПОВТОРЯЕМОМ коде (`REPEATABLE_ACHIEVEMENT_CODES`)
 * этой ставки быть не может. Повторяемый код + обход потолка = кран без рельса:
 * человек получает 500 столько раз, сколько нажмёт кнопку, без суточного
 * ограничителя. Сторожит `achievements.test.ts`.
 */
export const ACHIEVEMENT_MAJOR_REWARD = 500;

/**
 * Ставки всех 30 кодов Портала (`App\Enums\AchievementsEnum`).
 *
 * До 26.08.2026 здесь было семь кодов — те, у которых в `AchievementSeeder`
 * заполнено `loyalty_points`. Остальные 23 Портал не отправлял вовсе: старый
 * `ProcessAchievementUnlock` слал событие только при `loyalty_points > 0`.
 * Ветка `iec-id-integration` этот гейт снимает — ставку теперь назначаем мы, —
 * поэтому каталог обязан покрыть весь `AchievementsEnum`, иначе 23 кода начнут
 * получать `achievement_code_unknown`: человек увидит бейдж и не увидит Баллов.
 *
 * ВНИМАНИЕ: сами ставки 23 новых кодов ПРЕДЛОЖЕНЫ, а не утверждены. Эмиссия —
 * решение владельца; до подтверждения таблица считается черновиком (строка в
 * `docs/LAUNCH-REGISTRY.md` §6.4).
 *
 * Как выбрана ступень (`rating_points` Портала — подсказка о весе, но не
 * формула: у `buddy_tag` рейтинг 30 и ставка 500, у `50_reactions_on_post`
 * рейтинг 50 и ставка 100 — решает смысл, а рейтинг разводит соседей внутри
 * одной группы):
 *   • якорь (50) — массовое, легко повторяемое действие, нижний порог группы;
 *   • заметное (100) — верхний порог группы, требует накопленной аудитории;
 *   • весомое (500) — редкое, подтверждается редакцией или деньгами.
 *
 * ЧЕГО ПОТОЛОК ЗДЕСЬ НЕ ЗАКРЫВАЕТ (не считать эту таблицу защищённой). Суточный
 * потолок — суточный, а каждое достижение выдаётся раз в жизни, поэтому набор
 * бейджей он не запрещает, а лишь растягивает по дням. Кольцо фейковых
 * аккаунтов, подписывающихся друг на друга и переписывающихся между собой,
 * закрывает реакции, подписчиков, репосты, чаты, комментарии и серии — около
 * двадцати кодов без единого настоящего читателя. Античит есть только у
 * рефералов (`referrals/_internal/anticheat.ts`); у достижений его нет, и
 * решение по нему — за владельцем (`docs/LAUNCH-REGISTRY.md` §6.4).
 */
export const ACHIEVEMENT_REWARDS = {
	// ─── Рядовые: разовое действие, подтверждающее вовлечённость ───
	webinar_registration: ACHIEVEMENT_BASE_REWARD,
	contest_participation: ACHIEVEMENT_BASE_REWARD,
	all_social_media: ACHIEVEMENT_BASE_REWARD,
	survey_completion: ACHIEVEMENT_BASE_REWARD,
	/** Первый вопрос в ленте — порог входа, дешевле уже некуда. */
	first_question: ACHIEVEMENT_BASE_REWARD,
	// ─── Нижние пороги контентных групп: набираются одним удачным постом ───
	"10_reactions_on_post": ACHIEVEMENT_BASE_REWARD,
	"25_reactions_on_post": ACHIEVEMENT_BASE_REWARD,
	"5_followers": ACHIEVEMENT_BASE_REWARD,
	"15_followers": ACHIEVEMENT_BASE_REWARD,
	/**
	 * Три кода репостов Портал НЕ ОТПРАВЛЯЕТ: репостов в нём нет, а
	 * `AchievementService::checkRepostsAchievements` закомментирован целиком.
	 * Ставки заведены на будущее — ровно чтобы день появления репостов не начался
	 * с `achievement_code_unknown`, — но в расчёт реальной эмиссии эти три кода
	 * (как и `first_payment`) входить не должны.
	 */
	"5_reposts_on_post": ACHIEVEMENT_BASE_REWARD,
	"10_reposts_on_post": ACHIEVEMENT_BASE_REWARD,
	"5_posts_in_series": ACHIEVEMENT_BASE_REWARD,
	question_10_comments: ACHIEVEMENT_BASE_REWARD,
	/**
	 * Переписки — самое дешёвое в производстве действие Портала: тридцать чатов
	 * заводятся за вечер. Держим на якоре вопреки рейтингу 35, иначе это первое,
	 * что начнут фармить.
	 */
	"30_chats": ACHIEVEMENT_BASE_REWARD,
	/**
	 * «1000 просмотров поста» выглядит как охват, но им не является: Портал
	 * миграцией `2025_10_27_120000_refactor_post_views_to_store_individual_views`
	 * СНЯЛ `unique(user_id, post_id)` с `post_views`, `PostViewService::trackView`
	 * пишет строку безусловно и автора не исключает, а достижение считает сырые
	 * строки. Тысяча обращений к собственному посту — и бейдж выдан. Держим на
	 * якоре по той же причине, что и `30_chats`; поднять до заметной ступени
	 * можно после дедупликации просмотров на стороне Портала.
	 */
	"1000_views_on_post": ACHIEVEMENT_BASE_REWARD,
	// ─── Заметные: верхние пороги групп, нужна накопленная аудитория ───
	"50_reactions_on_post": ACHIEVEMENT_NOTABLE_REWARD,
	"25_followers": ACHIEVEMENT_NOTABLE_REWARD,
	"30_followers": ACHIEVEMENT_NOTABLE_REWARD,
	"15_reposts_on_post": ACHIEVEMENT_NOTABLE_REWARD,
	"10_posts_in_series": ACHIEVEMENT_NOTABLE_REWARD,
	"15_posts_in_series": ACHIEVEMENT_NOTABLE_REWARD,
	question_20_comments: ACHIEVEMENT_NOTABLE_REWARD,
	question_30_comments: ACHIEVEMENT_NOTABLE_REWARD,
	longread_20_reactions: ACHIEVEMENT_NOTABLE_REWARD,
	/** Пост отобран редакцией в «Лучшее» — не фармится объёмом, отбор ручной. */
	post_in_best_section: ACHIEVEMENT_NOTABLE_REWARD,
	/**
	 * Анкета на программу — сильнейший сигнал намерения заплатить, и по смыслу
	 * это весомая ступень. Стоит на заметной ВЫНУЖДЕННО: код ПОВТОРЯЕМЫЙ
	 * (`AchievementsEnum::isRepeatable()`, `REPEATABLE_ACHIEVEMENT_CODES`), а
	 * `AchievementService::checkProgramApplicationFormAchievement` шлёт событие
	 * на КАЖДУЮ попытку подачи без всякой проверки. На ступени 500 это
	 * потребовало бы записи в `ONE_TIME_ACHIEVEMENT_CODES` (иначе первая же
	 * анкета урезалась бы потолком free), а обход потолка на повторяемом коде —
	 * это 500 Баллов ($5) за каждое нажатие «отправить», без верхней границы.
	 * 100 под суточным потолком фарм ограничивает, поэтому ставка такая.
	 * Поднимать её можно только вместе с ограничением выдачи на стороне Портала
	 * (`docs/LAUNCH-REGISTRY.md` §6.4).
	 */
	program_application_form: ACHIEVEMENT_NOTABLE_REWARD,
	/**
	 * Три попадания в «Лучшее»: высший рейтинг всей контентной шкалы Портала (70)
	 * и трижды подтверждённый редакцией вклад. По смыслу — весомая ступень, но
	 * ставка удержана на заметной, пока Портал не научится его считать:
	 * `AchievementService::checkThreePostsInKnowledgeBaseAchievement` — дословная
	 * копия проверки `post_in_best_section` (тот же `$isFirst` по тому же
	 * `contentable_id`), три материала она НЕ считает. Оба достижения выдаются
	 * одним и тем же первым одобрением редакции, то есть на ступени 500 первый же
	 * одобренный пост давал бы 100 + 500 = 600 Баллов мимо суточного потолка.
	 * Поднять до `ACHIEVEMENT_MAJOR_REWARD` (вместе со строкой в
	 * `ONE_TIME_ACHIEVEMENT_CODES`) — после починки Портала.
	 */
	"3_times_in_best": ACHIEVEMENT_NOTABLE_REWARD,
	// ─── Весомые: требуют реального вклада или продвигают человека к оплате ───
	expert_role: ACHIEVEMENT_MAJOR_REWARD,
	buddy_tag: ACHIEVEMENT_MAJOR_REWARD,
	/**
	 * Оплаченная программа — единственное достижение, за которым стоят настоящие
	 * деньги, поэтому высшая ступень.
	 *
	 * ПОРТАЛ ЕГО ПОКА НЕ ВЫДАЁТ: `first_payment` встречается в `backend/app`
	 * ровно один раз — как `case` перечисления, места выдачи нет. Код заведён
	 * здесь на будущее, чтобы день включения не начался с
	 * `achievement_code_unknown`; до того дня ставка ни на что не влияет. Когда
	 * выдачу напишут, она обязана быть однократной (`isUnlocked`, как у
	 * `expert_role`): код лежит в `ONE_TIME_ACHIEVEMENT_CODES` и потолок обходит.
	 */
	first_payment: ACHIEVEMENT_MAJOR_REWARD,
} as const;

export type AchievementCode = keyof typeof ACHIEVEMENT_REWARDS;

/**
 * Сколько Баллов даёт достижение. `null` — код неизвестен, начисления нет.
 *
 * `Object.hasOwn`, а не оператор `in`: `in` идёт по цепочке прототипов, и код
 * `toString` из чужого события возвращал не `null`, а функцию `Object.prototype`.
 * Прослежено до конца: функция уезжала в `points`, умножение на множитель давало
 * `NaN`, ни одна проверка `<= 0` его не отсекала, и начисление падало уже на
 * `INSERT` в integer-колонку `amount` — то есть чужой код ронял запись вместо
 * честного отказа `achievement_code_unknown`.
 */
export function achievementReward(code: string): number | null {
	return Object.hasOwn(ACHIEVEMENT_REWARDS, code)
		? ACHIEVEMENT_REWARDS[code as AchievementCode]
		: null;
}

/**
 * Достижения, которые Портал выдаёт человеку НЕ ОДИН раз.
 *
 * Зеркало `App\Enums\AchievementsEnum::isRepeatable()`. Копия, а не импорт —
 * репозитории разные; расхождение сторожит `achievements.test.ts`, а не
 * добросовестность правящего.
 *
 * ЗАЧЕМ ЭТОТ СПИСОК ЗДЕСЬ ВООБЩЕ. Он задаёт два запрета, и оба про деньги:
 *   1. повторяемый код не может лежать в `ONE_TIME_ACHIEVEMENT_CODES` — иначе
 *      обход суточного потолка превращается в кран без верхней границы;
 *   2. повторяемый код не может стоить дороже суточного потолка free — иначе
 *      каждая выдача урезается остатком дня, и обещание витрины недостижимо.
 * Вместе они означают: повторяемое достижение живёт под дросселем, как обычная
 * активность, и именно дроссель ограничивает его эмиссию.
 */
export const REPEATABLE_ACHIEVEMENT_CODES = new Set<string>([
	"webinar_registration",
	"contest_participation",
	"survey_completion",
	"program_application_form",
]);

/**
 * Разделитель кода достижения и повода внутри `referenceId`.
 *
 * Символа `/` нет ни в одном коде `AchievementsEnum` (там только буквы, цифры и
 * `_`), поэтому первая его встреча всегда отделяет код от повода. Двоеточие для
 * этой роли не годится: конструктор ключей идемпотентности (`idempotency.ts`)
 * заменяет `:` внутри частей на `_`, и разбор обратно стал бы неоднозначным.
 */
export const ACHIEVEMENT_REFERENCE_SEPARATOR = "/";

/**
 * Ссылка достижения для ключа идемпотентности: код плюс повод конкретной выдачи.
 *
 * ПОЧЕМУ НЕ ОДИН КОД. Ключ класса B даты не содержит (в этом весь смысл: пара
 * «причина + ссылка» оплачивается один раз за всё время). Для повторяемых
 * достижений Портала — регистрация на вебинар, участие в конкурсе, опрос,
 * анкета на программу — из одного кода получается один ключ на всю жизнь
 * аккаунта: десять вебинаров дают одно начисление, а девять оставшихся событий
 * получают `duplicate: true`, то есть УСПЕХ, без ошибки и без записи в логе.
 * Портал этот повод присылает (`metadata.reference_id`,
 * `IecIdLoyaltyEventSender::achievementUnlocked`) — здесь он попадает в ключ.
 *
 * Повод у одноразовых достижений Портал не шлёт, но если пришлёт (чужой сервис,
 * ошибка места выдачи), склейка всё равно безопасна: `achievementCodeOf`
 * вытаскивает код обратно, и решение «обходит ли потолок» принимается по коду.
 */
export function achievementReferenceId(code: string, reference?: string | null): string {
	return reference ? `${code}${ACHIEVEMENT_REFERENCE_SEPARATOR}${reference}` : code;
}

/**
 * Обратная операция: код достижения из `referenceId` записи ledger.
 *
 * Нужна двум местам, и оба ломаются молча без неё: крану (решение об обходе
 * потолка принимается по коду, а не по коду с поводом) и витрине
 * (`history-mapper`, ICU-`select` по коду — иначе все повторяемые достижения
 * показывались бы безымянным «Достижение»).
 */
export function achievementCodeOf(referenceId: string): string {
	const at = referenceId.indexOf(ACHIEVEMENT_REFERENCE_SEPARATOR);
	return at === -1 ? referenceId : referenceId.slice(0, at);
}

/**
 * Разовые награды, ограниченные КОНСТРУКЦИЕЙ, а не суточным дросселем.
 *
 * Логика та же, что у вех обучения (план §4.2): выплата случается один раз на
 * человека — её предельная эмиссия известна заранее (сумма × число людей), и
 * дроссель ей не нужен. Без этого исключения обещание «+500 Баллов за
 * настройку» ломалось о потолок free-уровня в 300 и человек получал меньше,
 * чем ему показали на экране.
 *
 * Гарантия однократности — не этот список, а `idempotency_key` записи ledger:
 * ключ активности не содержит даты, поэтому пара «причина + ссылка» оплачивается
 * ровно один раз за всё время.
 */
export const ONE_TIME_ACTIVITY_REASONS = new Set<string>([
	"onboarding_completed",
	"photo_added",
	// Первый вход в сервис: пара «человек ↔ сервис» оплачивается один раз за всё
	// время, и предельная эмиссия известна заранее (число сервисов × ставка).
	// Без этой строки три сервиса за день упирались бы в суточный потолок free
	// (300), и третий вход оплачивался бы молча урезанным — человек увидел бы в
	// задаче «+100», а получил бы 100 из 300 минус уже начисленное.
	// Месячный бюджет класса B при этом продолжает действовать: он рельс против
	// эмиссии, а не против однократных наград.
	"service_activated",
]);

/**
 * Достижения, которые случаются один раз в жизни аккаунта, а не «активность».
 *
 * Решение оркестратора от 21.08.2026 по конфликту Д6: суточный потолок free
 * равен 300, а эти достижения стоят 500 — на free они молча урезались бы, и
 * человек получал бы меньше, чем показала витрина. Стать экспертом и получить
 * бадди-метку нельзя «ещё раз завтра», поэтому они ограничены однократностью,
 * как онбординг, а не дросселем. Потолок free при этом остаётся 300 — он рельс
 * против ФЕРМЫ повторяемых событий.
 *
 * ГРАНИЦА СПИСКА — С ДВУХ СТОРОН, и обе машинные (`achievements.test.ts`):
 *   • попасть сюда ОБЯЗАН каждый код дороже суточного потолка free, иначе
 *     витрина разойдётся с начислением;
 *   • попасть сюда НЕ МОЖЕТ ни один код из `REPEATABLE_ACHIEVEMENT_CODES` —
 *     обход потолка на повторяемой выдаче снимает единственный ограничитель
 *     эмиссии. Именно по этой причине здесь больше нет
 *     `program_application_form`: Портал выдаёт его на каждую попытку подачи.
 *
 * Проверяется по КОДУ ДОСТИЖЕНИЯ, а не по причине: до крана достижения доезжают
 * с `reasonCode = ACHIEVEMENT_REASON_CODE`, а код лежит в `referenceId` (у
 * повторяемых — вместе с поводом, отсюда `achievementCodeOf`). Список причин
 * здесь бы никогда не совпал — правка была бы декоративной (грабли №14).
 */
export const ONE_TIME_ACHIEVEMENT_CODES = new Set<string>([
	"expert_role",
	"buddy_tag",
	// Предложено 26.08.2026 вместе с остальными кодами Портала: событие, которое
	// в жизни аккаунта случается ровно одно. Ставка ждёт подтверждения владельца,
	// см. `docs/LAUNCH-REGISTRY.md` §6.4.
	"first_payment",
]);

/**
 * Причина класса B, под которой приезжают достижения. Ставка у них берётся не по
 * причине, а по коду в `metadata.achievement_code` (`ACHIEVEMENT_REWARDS`).
 *
 * Живёт в `shared`, хотя разбирает событие модуль `events`: `bypassesDailyCap`
 * обязан отличать достижение от любой другой причины со ссылкой, а `shared` в
 * `events` смотреть не может.
 */
export const ACHIEVEMENT_REASON_CODE = "achievement";

/**
 * Обходит ли награда суточный потолок класса B.
 *
 * ПРИЧИНА ПРОВЕРЯЕТСЯ ПЕРВОЙ, И ЭТО НЕ ФОРМАЛЬНОСТЬ. `referenceId` у класса B
 * заполняется чем угодно: `readReferenceId` берёт `templateId`, `courseId`,
 * `programId`, `id` и ещё несколько имён из `metadata` ЛЮБОГО сервиса. Без
 * сверки с `ACHIEVEMENT_REASON_CODE` событие `course_published` (200 Баллов) с
 * `metadata.id = "first_payment"` обходило бы суточный потолок — вход в кран
 * открывался бы строкой, которую присылает сам сервис.
 */
export function bypassesDailyCap(reasonCode: string, referenceId?: string | null): boolean {
	if (ONE_TIME_ACTIVITY_REASONS.has(reasonCode)) return true;
	if (reasonCode !== ACHIEVEMENT_REASON_CODE) return false;
	return (
		typeof referenceId === "string" &&
		ONE_TIME_ACHIEVEMENT_CODES.has(achievementCodeOf(referenceId))
	);
}

/** Сколько Баллов даёт причина класса B. `null` — причина неизвестна. */
export function activityReward(reasonCode: string): number | null {
	// `Object.hasOwn` по той же причине, что и в `achievementReward`: оператор
	// `in` признал бы известной причину `toString`.
	return Object.hasOwn(ACTIVITY_REWARDS, reasonCode)
		? ACTIVITY_REWARDS[reasonCode as ActivityReasonCode]
		: null;
}

/**
 * Вехи реферальной программы: число **ПЛАТЯЩИХ** приглашённых → разовый бонус.
 *
 * Считаются платящие, а не активированные. Прежняя редакция платила за
 * активации: 50 активаций давали 90 000 Баллов ($900) сверх поштучных
 * начислений, при том что активация — это полезное действие, а не выручка.
 * Такая веха не финансируется ничем и держится только на античите; веха от
 * платящих финансируется маржой тех самых платежей и потому остаётся классом A
 * честно, а не по названию.
 *
 * Пороги снижены соответственно: 25 приглашённых, которые ЗАПЛАТИЛИ, — это
 * другой порядок усилия, чем 25 зарегистрировавшихся.
 */
export const REFERRAL_MILESTONES = [
	{ friends: 3, reward: 1500 },
	{ friends: 10, reward: 6000 },
	{ friends: 25, reward: 18_000 },
	{ friends: 50, reward: 40_000 },
] as const;

/** Античит: лимиты реферальной программы. */
export const REFERRAL_LIMITS = {
	perMonth: 50,
	total: 200,
} as const;

/** Виды записей ledger. Раздельные `kind` нужны, чтобы считать бюджеты. */
export const POINTS_ENTRY_KINDS = [
	"earn_referral",
	"earn_activity",
	"earn_milestone",
	"grant",
	"reverse",
	"expire",
	"migrate",
	"adjust",
] as const;

export type PointsEntryKind = (typeof POINTS_ENTRY_KINDS)[number];

/** Перевод Баллов в деньги (minor units) по фиксированному курсу. */
export function pointsToMinor(points: number): number {
	return Math.round((points / POINTS_PER_USD) * 100);
}

/**
 * Деньги из major units (доллары) в minor units (центы) — инвариант №8.
 *
 * Нужно на границе с чужими системами: Портал шлёт `program_price` как PHP-float
 * в долларах (`(float) $pp->Billing_TotalDue`), а внутрь нашего контура целые
 * центы попадают только через эту функцию.
 *
 * ПРАВИЛО ОКРУГЛЕНИЯ — до цента, половина вверх. Прямое `value * 100` брать
 * нельзя: в double `1.005 * 100 === 100.49999999999999`, и цент теряется молча.
 * `toPrecision(15)` снимает двоичный хвост ДО округления, поэтому `1.005 → 101`,
 * а `1250.50 → 125050`.
 */
export function majorToMinor(value: number): number {
	if (!Number.isFinite(value)) return Number.NaN;
	return Math.round(Number.parseFloat((value * 100).toPrecision(15)));
}

/** Сколько Баллов нужно, чтобы закрыть указанную сумму (minor units). */
export function minorToPoints(minor: number): number {
	return Math.round((minor / 100) * POINTS_PER_USD);
}

/** Максимум Баллов, которыми можно закрыть счёт с учётом доли SKU. */
export function maxPointsForCheck(
	eligibleMinor: number,
	sharePct: number = DEFAULT_MAX_POINTS_SHARE_PCT,
): number {
	return minorToPoints(Math.floor((eligibleMinor * sharePct) / 100));
}
